import { z } from 'zod';

// ============================================================================
// Project Schemas
// ============================================================================

/**
 * Schema for project settings - authentication configuration
 */
export const AuthenticationSettingsSchema = z.object({
  global_settings: z.object({
    session_timeout: z.number().int().min(1).max(86400), // 1 second to 24 hours
    max_sessions: z.number().int().min(1).max(100),
  }).optional(),
  passwordless_login: z.object({
    enabled: z.boolean(),
    methods: z.array(z.enum(['webauthn', 'email', 'sms'])),
  }).optional(),
});

export type AuthenticationSettings = z.infer<typeof AuthenticationSettingsSchema>;

/**
 * Schema for project settings - identity proofing configuration
 */
export const IdentityProofingSettingsSchema = z.object({
  face_verification: z.object({
    enabled: z.boolean(),
    threshold: z.number().min(0).max(1),
  }).optional(),
  universal_web: z.object({
    enabled: z.boolean(),
    verification_level: z.enum(['basic', 'enhanced', 'strict']),
  }).optional(),
});

export type IdentityProofingSettings = z.infer<typeof IdentityProofingSettingsSchema>;

/**
 * Schema for project settings - integrity configuration
 */
export const IntegritySettingsSchema = z.object({
  android: z.object({
    enabled: z.boolean(),
    app_integrity_enabled: z.boolean(),
    device_integrity_enabled: z.boolean(),
  }).optional(),
  web: z.object({
    enabled: z.boolean(),
    recaptcha_enabled: z.boolean(),
    recaptcha_site_key: z.string().optional(),
  }).optional(),
});

export type IntegritySettings = z.infer<typeof IntegritySettingsSchema>;

/**
 * Schema for project settings - network rules configuration
 */
export const NetworkRulesSettingsSchema = z.object({
  ip_blocking: z.object({
    enabled: z.boolean(),
    blocked_ips: z.array(z.string().ip()),
    allowed_ips: z.array(z.string().ip()),
  }).optional(),
  country_restrictions: z.object({
    enabled: z.boolean(),
    blocked_countries: z.array(z.string().length(2)), // ISO country codes
    allowed_countries: z.array(z.string().length(2)),
  }).optional(),
});

export type NetworkRulesSettings = z.infer<typeof NetworkRulesSettingsSchema>;

/**
 * Schema for complete project settings
 */
export const ProjectSettingsSchema = z.object({
  authentication: AuthenticationSettingsSchema.optional(),
  identity_proofing: IdentityProofingSettingsSchema.optional(),
  integrity: IntegritySettingsSchema.optional(),
  network_rules: NetworkRulesSettingsSchema.optional(),
});

export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;

/**
 * Schema for project entity
 */
export const ProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  tenant_id: z.string().uuid(), // Legacy field - maps to organization_id
  organization_id: z.string().uuid(),
  settings: ProjectSettingsSchema.optional(),
  status: z.enum(['active', 'inactive', 'suspended']).default('active'),
  metadata: z.record(z.unknown()).optional(),
});

export type Project = z.infer<typeof ProjectSchema>;

/**
 * Schema for project list response
 */
export const ProjectListSchema = z.array(ProjectSchema);

export type ProjectList = z.infer<typeof ProjectListSchema>;

// ============================================================================
// Project Form Schemas
// ============================================================================

/**
 * Schema for creating a new project
 */
export const CreateProjectSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be less than 255 characters'),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  organization_id: z.string().uuid(),
  settings: ProjectSettingsSchema.optional(),
});

export type CreateProject = z.infer<typeof CreateProjectSchema>;

/**
 * Schema for updating an existing project
 */
export const UpdateProjectSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be less than 255 characters').optional(),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  settings: ProjectSettingsSchema.optional(),
  status: z.enum(['active', 'inactive', 'suspended']).optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type UpdateProject = z.infer<typeof UpdateProjectSchema>;

// ============================================================================
// Project Query Schemas
// ============================================================================

/**
 * Schema for project query parameters
 */
export const ProjectQueryParamsSchema = z.object({
  page: z.number().int().min(1).default(1),
  per_page: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  status: z.enum(['active', 'inactive', 'suspended']).optional(),
  sort_by: z.enum(['name', 'created_at', 'updated_at']).default('created_at'),
  sort_order: z.enum(['asc', 'desc']).default('desc'),
});

export type ProjectQueryParams = z.infer<typeof ProjectQueryParamsSchema>;

// ============================================================================
// Pagination Schema
// ============================================================================

/**
 * Schema for paginated project response
 */
export const PaginatedProjectResponseSchema = z.object({
  data: ProjectListSchema,
  pagination: z.object({
    page: z.number().int().min(1),
    per_page: z.number().int().min(1),
    total: z.number().int().min(0),
    total_pages: z.number().int().min(0),
  }),
});

export type PaginatedProjectResponse = z.infer<typeof PaginatedProjectResponseSchema>;

// ============================================================================
// Error Schemas
// ============================================================================

/**
 * Schema for project API error response
 */
export const ProjectErrorSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
    field: z.string().optional(),
    details: z.record(z.unknown()).optional(),
  }),
});

export type ProjectError = z.infer<typeof ProjectErrorSchema>;

/**
 * Schema for project validation error
 */
export const ProjectValidationErrorSchema = z.object({
  errors: z.array(z.object({
    field: z.string(),
    message: z.string(),
    code: z.string().optional(),
  })),
  message: z.string(),
});

export type ProjectValidationError = z.infer<typeof ProjectValidationErrorSchema>;

// ============================================================================
// Project Statistics Schema
// ============================================================================

/**
 * Schema for project statistics
 */
export const ProjectStatisticsSchema = z.object({
  total_validations: z.number().int().min(0),
  successful_validations: z.number().int().min(0),
  failed_validations: z.number().int().min(0),
  pending_validations: z.number().int().min(0),
  last_validation_at: z.string().datetime().nullable(),
  created_at: z.string().datetime(),
});

export type ProjectStatistics = z.infer<typeof ProjectStatisticsSchema>;

/**
 * Schema for project with statistics
 */
export const ProjectWithStatisticsSchema = ProjectSchema.extend({
  statistics: ProjectStatisticsSchema.optional(),
});

export type ProjectWithStatistics = z.infer<typeof ProjectWithStatisticsSchema>;